import type { Organizationschema, OrganizationsListResponse } from '@app/types/sdk';
import type { MockContext } from '@mocks/mockFetch';

const createOrganization = (partial: Partial<Organizationschema>): Organizationschema => {
  const deriveSlug = (value: string | undefined | null): string | undefined => {
    if (!value) {
      return undefined;
    }
    return value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  return {
    name: partial.name ?? 'Organization',
    slug: partial.slug ?? deriveSlug(partial.name ?? undefined),
    notes: partial.notes,
    id: partial.id,
    createdAt: partial.createdAt ?? new Date(),
    updatedAt: partial.updatedAt ?? new Date(),
    createdBy: partial.createdBy ?? 'system',
    updatedBy: partial.updatedBy ?? 'system'
  } as Organizationschema;
};

const SAMPLE_ORGANIZATIONS: Organizationschema[] = [
  createOrganization({
    id: '11111111-1111-4111-8111-111111111111',
    name: 'Arcadia Networks',
    slug: 'arcadia-networks',
    createdAt: new Date('2024-01-11T14:20:00.000Z'),
    updatedAt: new Date('2024-03-02T09:15:00.000Z')
  }),
  createOrganization({
    id: '22222222-2222-4222-8222-222222222222',
    name: 'Reap Labs Internal',
    slug: 'reap-labs-internal',
    createdAt: new Date('2024-02-05T17:40:00.000Z'),
    updatedAt: new Date('2024-03-14T11:05:00.000Z')
  }),
  createOrganization({
    id: '33333333-3333-4333-8333-333333333333',
    name: 'Nimbus Field Ops',
    slug: 'nimbus-field-ops',
    createdAt: new Date('2024-01-24T07:30:00.000Z'),
    updatedAt: new Date('2024-02-28T21:50:00.000Z')
  })
];

const ORGANIZATIONS: Organizationschema[] = [...SAMPLE_ORGANIZATIONS];

export const orgHandlers = [
  async ({ request, url }: MockContext) => {
    if (!url.pathname.endsWith('/orgs')) {
      return null;
    }

    if (request.method === 'GET') {
      const items: Organizationschema[] = ORGANIZATIONS;

      const body: OrganizationsListResponse = {
        items,
        meta: {
          limit: items.length,
          next: undefined,
          prev: undefined
        }
      };

      return new Response(JSON.stringify(body), {
        status: 200,
        headers: {
          'Content-Type': 'application/json',
          'X-Mock-Route': 'orgs'
        }
      });
    }

    if (request.method === 'POST') {
      try {
        const payload = (await request.clone().json()) as Partial<Organizationschema>;
        const generatedId =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `mock-org-${Date.now()}`;
        const created = createOrganization({
          id: generatedId,
          name: payload.name ?? 'New Organization',
          slug: payload.slug ?? undefined,
          notes: payload.notes,
          createdAt: new Date(),
          updatedAt: new Date(),
          createdBy: 'mock-user',
          updatedBy: 'mock-user'
        });

        if (!ORGANIZATIONS.some((org) => org.id === created.id)) {
          ORGANIZATIONS.push(created);
        }

        (globalThis as Record<string, any>).__mockLastCreatedOrg = created;

        return new Response(JSON.stringify(created), {
          status: 201,
          headers: {
            'Content-Type': 'application/json',
            'X-Mock-Route': 'orgs',
            Location: `/public/v1/orgs/${created.id}`
          }
        });
      } catch (error) {
        console.warn('[mock:orgs] failed to parse create payload', error);
        return new Response(JSON.stringify({ message: 'Invalid mock payload' }), {
          status: 400,
          headers: {
            'Content-Type': 'application/json',
            'X-Mock-Route': 'orgs'
          }
        });
      }
    }

    return null;
  }
];
